import React, {useRef, useReducer, useState, useCallback, useEffect} from 'react';
import {getGuidePoint} from './getGuidePoint.js';
import AllLinePoints from './AllLinePoints.js';
import AutomationSelection from './AutomationSelection.js';
import GuideLines from './GuideLines.js';
import {
    setAutomationPointDragging,
    setAutomationPointsSelected,
    setAutomationSelection,
    setAutomationPointHasMoved,
    setAutomationGuidePoint,
} from 'actions/index.js';
import { connect } from "react-redux";
import {onClickLine} from './onClickLine.js';
import AutomationPoints from './AutomationPoints.js';
import {
    addAutomationBreakpoint,
    removeAutomationBreakpoint,
    moveAutomationBreakpoint,
    moveAutomationBreakPoints
} from 'operations/actions.js';
import {
    getYPositionFromValue,
    getYForParameter,
    getXPositionFromStep,
    getStepFromXPosition,
    getValueFromYPosition
} from './position.js';
import { Circle, Group, Stage, Layer, Rect, Line} from 'react-konva';
import LinePoints from './LinePoints.js';
import {getLinePoints} from './getLinePoints.js';
import {reducer, init} from './reducer.js';
import GuidePoint from './GuidePoint.js';
import {AutomationContext} from './context.js';
import '../../Automation.scss';

function ParameterAutomation(props) {
    let sequencerEngine = props.track.sequencerEngine;
    let operationManager = sequencerEngine.operationManager;
    let ref = props.wrapperRef;
    let svgRef = useRef(undefined);
    let automationRef = useRef(undefined);
    const {setGuidePoint, setPointHasMoved, selectedParameter, height, automation, track, xScale} = props;
    const [state, dispatch] = useReducer(reducer, props.automation, init);
    const [svgLeft, setSvgLeft] = useState(props.left || 56);
    const {selectedPoint} = state;
    let width = props.width || (
        track.pattern &&
            getXPositionFromStep(track.pattern.length(), xScale)) || 0;

    const onMouseDown= useCallback((e) => {
        let rect = svgRef.current.getBoundingClientRect();
        let x = e.clientX - svgLeft + ref.current.scrollLeft;
        let y = e.clientY - rect.top;
        props.setSelection({x, y, height: 0, width: 0});
    }, [clear, svgLeft]);

    const onMouseMove = useCallback((e) => {
        if (selectedParameter === undefined) {
            return;
        }
        let {pointDragging, selection, pointsSelected, pointHovered} = sequencerEngine.state.automation;
        pointDragging = pointDragging[track.id];
        selection = selection[track.id];
        pointsSelected = pointsSelected[track.id] || [];
        pointHovered = pointHovered[track.id];

        let svgTop = svgRef.current.getBoundingClientRect()
            .top;
        let x = e.clientX - svgLeft + ref.current.scrollLeft;
        let y = e.clientY - svgTop;
        if (pointDragging) {
            let step = getStepFromXPosition(x, props.xScale, width);
            let value = getValueFromYPosition(
                y,
                height,
                selectedParameter.min,
                selectedParameter.max);

            if (pointsSelected.length > 0) {
                let stepDiff = step - pointDragging.step;
                let valueDiff = value - pointDragging.value;
                operationManager.dispatch(
                    moveAutomationBreakPoints(
                        props.automation, pointsSelected, stepDiff, valueDiff));
            } else {
                operationManager.dispatch(
                    moveAutomationBreakpoint(
                        props.automation, pointDragging, step, value));
            }
            setPointHasMoved(true);
        } else if (selection) {
            let width = x - selection.x;
            let height = y - selection.y;
            props.setSelection({... selection, width, height});
        } else if (pointHovered === undefined) {
            let x = props.isMix ? e.clientX - props.left : e.clientX - 56;
            let step = getStepFromXPosition(x + ref.current.scrollLeft, xScale, width);
            let value = automation.getValueAtStep(step);
            let {distance, point} = automation.getDistanceToClosest({step, value});
            if (distance < 0.1) {
                setGuidePoint(undefined);
            } else {
                let guidePoint = getGuidePoint(automation, step, selectedParameter, height);
                setGuidePoint(guidePoint);
            }
        } 
        
    }, [
        automation,
        xScale,
        track,
        selectedParameter,
        height,
        setPointHasMoved,
        setGuidePoint,
        svgLeft,
        width]);

    useEffect(() => {
        return () => props.setSelectedPoints([]);
    }, [selectedParameter]);

    const onMouseUp = useCallback(() => {
        let {pointDragging, pointHasMoved, selection, pointsSelected} = sequencerEngine.state.automation;
        pointDragging = pointDragging[track.id];
        pointHasMoved = pointHasMoved[track.id];
        selection = selection[track.id];
        pointsSelected = pointsSelected[track.id] || [];
        if (pointDragging && pointHasMoved) {
            clear();
            return;
        }
        if (selection && selectedParameter && !props.isMix) {
            let {x, y, width, height} = selection;
            let {min, max} = selectedParameter;
            let fromStep = getStepFromXPosition(x, xScale, width);
            let fromValue = getValueFromYPosition(y, props.height, min, max);
            let toStep = getStepFromXPosition(x + width, xScale, width);
            let toValue = getValueFromYPosition(y + height, props.height, min, max);
            let selectedPoints = automation.getSelectedPoints(
                Math.min(fromStep, toStep),
                Math.min(fromValue, toValue),
                Math.max(toStep, fromStep),
                Math.max(fromValue, toValue));
            props.setSelectedPoints(selectedPoints);
            props.setSelection(undefined);
        }
        clear();
    }, [clear, selectedParameter, automation, xScale, props.height, track, props.isMix]);

    const clear = useCallback(() => {
        props.setGuidePoint(undefined);
        props.setPointDragging(undefined);
        props.setSelection(undefined);
        setPointHasMoved(false);
    }, []);

    if (selectedParameter === undefined && props.isMix) {
        return [];
    }
    return (
        <div
          style={{width: width}}
          onMouseUp={onMouseUp}
          ref={automationRef}
          onMouseLeave={clear}
          id="automation-svg-container"
          className="parameter-automation">
          <svg
            ref={svgRef}
            pixelRatio={1}
            width={width}
            height={height}
            onMouseMove={onMouseMove}
            onMouseDown={onMouseDown}
            >
            {!props.isMix ? <AutomationSelection track={track}/> : ''}
            <GuideLines
              width={width}
              selectedParameter={selectedParameter}
              height={height}
              xScale={xScale}/>
            {!props.isMix ? 
            <AllLinePoints
              track={track}
              color={props.color}
              sequencerEngine={sequencerEngine}
              selectedParameter={selectedParameter}
              xScale={xScale}
              isMix={props.isMix}
              width={width}
              height={height}
            /> : ''}
            <LinePoints
              wrapperRef={ref}
              isMix={props.isMix}
              track={track}
              color={props.color}
              sequencerEngine={sequencerEngine}
              automation={automation}
              selectedParameter={selectedParameter}
              xScale={xScale}
              width={width}
              height={height}
            />
            <GuidePoint
              width={width}
              isMix={props.isMix}
              wrapperRef={ref}
              selectedParameter={selectedParameter}
              track={props.track}
              dispatch={dispatch}
              xScale={xScale}
              width={width}
              height={height}
            />
          </svg>
        </div>
    );
}

const mapStateToProps = (state, props) => {
    let selectedParameter = state.automation.trackParameter[props.track.id];
    return {
        numEffects: props.track.effects && props.track.effects.length,
        height: props.height || state.arrangement.bottomHeight - 250,
        selectedDevice: state.automation.trackDevice[props.track.id],
        selectedParameter: selectedParameter,
        automation: selectedParameter && (props.isMix ? selectedParameter.mixAutomation : selectedParameter.automation),
        // value: selectedParameter && selectedParameter.get(),
    };
};

const mapDispatchToProps = (dispatch, props) => {
    return {
        setPointDragging: () => dispatch(setAutomationPointDragging(props.track.id, undefined)),
        setGuidePoint: (point) => dispatch(setAutomationGuidePoint(props.track.id, point)),
        setPointHasMoved: (hasMoved) => dispatch(setAutomationPointHasMoved(props.track.id, hasMoved)),
        setSelection: (selection) => dispatch(setAutomationSelection(props.track.id, selection)),
        setSelectedPoints: (selected) => dispatch(setAutomationPointsSelected(props.track.id, selected))
    };
};


export default connect(mapStateToProps, mapDispatchToProps)(ParameterAutomation);
